import React from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Camera, Calendar, Folder, Star, ArrowRight, MapPin, CreditCard } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import UserMenu from '../components/UserMenu';
import { auth, bookings as bookingsApi } from '../lib/api';
import { formatINR, formatDate, getStoredUser } from '../lib/auth';

const STATUS_STYLES = {
  pending: 'secondary',
  confirmed: 'default',
  in_progress: 'default',
  delivered: 'default',
  completed: 'default',
  cancelled: 'destructive',
};

const ClientDashboard = () => {
  const [searchParams] = useSearchParams();
  const [user, setUser] = React.useState(getStoredUser());
  const [bookingList, setBookingList] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [payingId, setPayingId] = React.useState(null);

  const loadBookings = React.useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await bookingsApi.list();
      setBookingList(data.data || []);
    } catch (err) {
      toast.error('Could not load your bookings.');
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    auth.me().then(({ data }) => setUser(data)).catch(() => {});
    loadBookings();
  }, [loadBookings]);

  const handlePay = async (bookingId) => {
    setPayingId(bookingId);
    try {
      await bookingsApi.pay(bookingId);
      toast.success('Payment recorded - booking confirmed!');
      loadBookings();
    } catch (err) {
      toast.error('Payment failed. Please try again.');
    } finally {
      setPayingId(null);
    }
  };

  const [profileOpen, setProfileOpen] = React.useState(searchParams.get('openProfile') === '1');

  React.useEffect(() => {
    if (searchParams.get('openProfile') === '1') {
      setProfileOpen(true);
    }
  }, [searchParams]);
  const [profileForm, setProfileForm] = React.useState({ name: '', phone: '', current_password: '', password: '', password_confirmation: '' });
  const [savingProfile, setSavingProfile] = React.useState(false);

  React.useEffect(() => {
    if (profileOpen && user) {
      setProfileForm({ name: user.name || '', phone: user.phone || '', current_password: '', password: '', password_confirmation: '' });
    }
  }, [profileOpen, user]);

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    try {
      const payload = { name: profileForm.name, phone: profileForm.phone };
      if (profileForm.password) {
        payload.current_password = profileForm.current_password;
        payload.password = profileForm.password;
        payload.password_confirmation = profileForm.password_confirmation;
      }
      const { data } = await auth.updateProfile(payload);
      setUser(data);
      localStorage.setItem('pixnest_user', JSON.stringify(data));
      toast.success('Profile updated.');
      setProfileOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not update your profile.');
    } finally {
      setSavingProfile(false);
    }
  };

  const upcoming = bookingList.filter((b) => ['pending', 'confirmed', 'in_progress'].includes(b.status));
  const completed = bookingList.filter((b) => ['delivered', 'completed'].includes(b.status));

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b bg-background sticky top-0 z-50">
        <div className="px-6 md:px-12 lg:px-24 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-2" data-testid="logo-link">
              <Camera className="w-8 h-8" />
              <span className="font-playfair text-2xl font-bold">Pixnest</span>
            </Link>
            <div className="flex items-center space-x-4">
              <Link to="/photographers">
                <Button variant="ghost" size="sm" data-testid="find-photographers">Find Photographers</Button>
              </Link>
              <UserMenu user={user} />
              <Dialog open={profileOpen} onOpenChange={setProfileOpen}>
                <DialogContent data-testid="profile-dialog">
                  <DialogHeader>
                    <DialogTitle>My Profile</DialogTitle>
                  </DialogHeader>
                  <form className="space-y-4" onSubmit={handleSaveProfile}>
                    <div>
                      <label className="text-sm font-medium mb-2 block">Full Name</label>
                      <Input value={profileForm.name} onChange={(e) => setProfileForm({ ...profileForm, name: e.target.value })} data-testid="profile-name" />
                    </div>
                    <div>
                      <label className="text-sm font-medium mb-2 block">Phone</label>
                      <Input value={profileForm.phone} onChange={(e) => setProfileForm({ ...profileForm, phone: e.target.value })} data-testid="profile-phone" />
                    </div>
                    <div className="pt-2 border-t space-y-4">
                      <p className="text-sm text-muted-foreground">Leave blank to keep your current password.</p>
                      <div>
                        <label className="text-sm font-medium mb-2 block">Current Password</label>
                        <Input type="password" value={profileForm.current_password} onChange={(e) => setProfileForm({ ...profileForm, current_password: e.target.value })} data-testid="profile-current-password" />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="text-sm font-medium mb-2 block">New Password</label>
                          <Input type="password" value={profileForm.password} onChange={(e) => setProfileForm({ ...profileForm, password: e.target.value })} data-testid="profile-new-password" />
                        </div>
                        <div>
                          <label className="text-sm font-medium mb-2 block">Confirm</label>
                          <Input type="password" value={profileForm.password_confirmation} onChange={(e) => setProfileForm({ ...profileForm, password_confirmation: e.target.value })} data-testid="profile-confirm-password" />
                        </div>
                      </div>
                    </div>
                    <DialogFooter>
                      <Button type="submit" disabled={savingProfile} data-testid="save-profile-button">
                        {savingProfile ? 'Saving…' : 'Save Changes'}
                      </Button>
                    </DialogFooter>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
          </div>
        </div>
      </header>

      <div className="px-6 md:px-12 lg:px-24 py-12">
        {/* Welcome Section */}
        <div className="mb-12">
          <h1 className="font-playfair text-4xl md:text-5xl font-bold mb-2" data-testid="dashboard-title">
            Welcome Back{user?.name ? `, ${user.name.split(' ')[0]}` : ''}
          </h1>
          <p className="text-lg text-muted-foreground" data-testid="dashboard-subtitle">
            Manage your bookings and access your photography assets
          </p>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12" data-testid="quick-stats">
          <Card>
            <CardHeader><CardTitle className="text-base font-medium text-muted-foreground">Total Bookings</CardTitle></CardHeader>
            <CardContent><div className="font-playfair text-4xl font-bold" data-testid="stat-bookings">{bookingList.length}</div></CardContent>
          </Card>
          <Card>
            <CardHeader><CardTitle className="text-base font-medium text-muted-foreground">Upcoming</CardTitle></CardHeader>
            <CardContent><div className="font-playfair text-4xl font-bold" data-testid="stat-upcoming">{upcoming.length}</div></CardContent>
          </Card>
          <Card>
            <CardHeader><CardTitle className="text-base font-medium text-muted-foreground">Completed</CardTitle></CardHeader>
            <CardContent><div className="font-playfair text-4xl font-bold" data-testid="stat-completed">{completed.length}</div></CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            <section data-testid="bookings-section">
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-playfair text-3xl font-semibold" data-testid="bookings-title">My Bookings</h2>
                <Link to="/photographers">
                  <Button variant="outline" size="sm" data-testid="book-new">Book New</Button>
                </Link>
              </div>

              {loading && <p className="text-muted-foreground">Loading your bookings…</p>}
              {!loading && bookingList.length === 0 && (
                <Card><CardContent className="p-8 text-center text-muted-foreground" data-testid="no-bookings">
                  You haven't booked a photographer yet.
                </CardContent></Card>
              )}

              <div className="space-y-4">
                {bookingList.map((booking) => (
                  <Card key={booking.id} className="overflow-hidden hover:shadow-lg transition-shadow" data-testid={`booking-${booking.id}`}>
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between mb-4 gap-4">
                        <div>
                          <h3 className="font-playfair text-xl font-semibold" data-testid={`booking-type-${booking.id}`}>
                            {booking.service?.title || booking.event_type || 'Booking'}
                          </h3>
                          <div className="text-sm text-muted-foreground" data-testid={`photographer-${booking.id}`}>
                            with {booking.photographer?.business_name || booking.photographer?.user?.name}
                          </div>
                        </div>
                        <Badge variant={STATUS_STYLES[booking.status] || 'outline'} className="capitalize" data-testid={`status-${booking.id}`}>
                          {booking.status.replace('_', ' ')}
                        </Badge>
                      </div>

                      <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground mb-4">
                        <div className="flex items-center"><Calendar className="w-4 h-4 mr-2" /><span data-testid={`booking-date-${booking.id}`}>{formatDate(booking.event_date)}</span></div>
                        {booking.location && <div className="flex items-center"><MapPin className="w-4 h-4 mr-2" />{booking.location}</div>}
                        <div className="font-semibold text-foreground">{formatINR(booking.total_amount)}</div>
                      </div>

                      <div className="flex items-center gap-2 flex-wrap">
                        <Link to={`/vault/${booking.id}`}>
                          <Button size="sm" variant="outline" data-testid={`view-assets-${booking.id}`}>
                            <Folder className="w-4 h-4 mr-2" />
                            View Assets
                          </Button>
                        </Link>
                        {Number(booking.paid_amount) < Number(booking.total_amount) && booking.status !== 'cancelled' && (
                          <Button size="sm" onClick={() => handlePay(booking.id)} disabled={payingId === booking.id} data-testid={`pay-${booking.id}`}>
                            <CreditCard className="w-4 h-4 mr-2" />
                            {payingId === booking.id ? 'Processing…' : 'Pay Now'}
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </section>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1 space-y-6">
            <Card data-testid="quick-actions">
              <CardHeader>
                <CardTitle>Quick Actions</CardTitle>
                <CardDescription>Get things done faster</CardDescription>
              </CardHeader>
              <CardContent className="space-y-2">
                <Link to="/photographers">
                  <Button variant="outline" className="w-full justify-start" data-testid="action-book">
                    <Calendar className="w-4 h-4 mr-2" />
                    Book a Photographer
                  </Button>
                </Link>
                <Link to="/vault">
                  <Button variant="outline" className="w-full justify-start" data-testid="action-vault">
                    <Folder className="w-4 h-4 mr-2" />
                    Browse Vault
                  </Button>
                </Link>
              </CardContent>
            </Card>

            {completed.length > 0 && (
              <Card data-testid="review-prompt">
                <CardHeader>
                  <CardTitle className="flex items-center"><Star className="w-4 h-4 mr-2" />Leave a Review</CardTitle>
                  <CardDescription>Help other clients by rating your completed bookings from the vault.</CardDescription>
                </CardHeader>
                <CardContent>
                  <Link to={`/vault/${completed[0].id}`}>
                    <Button variant="outline" size="sm" className="w-full">
                      Go to latest delivery
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClientDashboard;
